import {
  ALTERNATIVE_MODES,
  AlternativeChoice,
  AlternativeMode,
  ChosenAlternative,
  DirectFlightTipData,
  EcoAccommodationTipData,
  HighEmissionActivityTipData,
  ReductionTip,
  TipType,
  TransportAlternativeTipData,
} from '../models/reduction-tip';
import {
  CommitmentStatus,
  ReductionActionCode,
  TripCommitment,
} from 'src/app/shared/model/reduction-action';

/**
 * Tip types carrying a quantified saving. The others (`trip_duration`,
 * `high_emission_activity`) only change the daily intensity or flag a hotspot,
 * so they stay informational and cannot be simulated.
 */
const SELECTABLE_TIP_TYPES: readonly TipType[] = [
  'transport_alternative',
  'direct_flight',
  'eco_accommodation',
];

export function isSelectableTip(tip: ReductionTip): boolean {
  return SELECTABLE_TIP_TYPES.includes(tip.type);
}

/**
 * The trip element a tip would rewrite — a transport, an accommodation, an
 * activity — or null for `trip_duration`, which rewrites none.
 */
export function getTipElementId(tip: ReductionTip): string | null {
  switch (tip.type) {
    case 'transport_alternative':
    case 'direct_flight':
      return (tip.data as TransportAlternativeTipData | DirectFlightTipData)
        .transportId;
    case 'eco_accommodation':
      return (tip.data as EcoAccommodationTipData).accommodationId;
    case 'high_emission_activity':
      return (tip.data as HighEmissionActivityTipData).activityId;
    default:
      return null;
  }
}

/**
 * The selection once `tipIds` are ticked or unticked.
 *
 * Ticking a tip unticks every other tip rewriting the same element — a flight
 * with a stopover gets both a ground alternative and a direct-flight tip, and
 * the trip can only take one. The selection therefore never holds two tips on
 * the same element, which the simulation and the apply rely on.
 */
export function updateTipSelection(
  current: ReadonlySet<string>,
  tips: readonly ReductionTip[],
  tipIds: readonly string[],
  selected: boolean,
): Set<string> {
  const next = new Set(current);
  if (!selected) {
    tipIds.forEach((id) => next.delete(id));
    return next;
  }

  tipIds.forEach((id) => {
    const tip = tips.find((candidate) => candidate.id === id);
    if (!tip) return;
    const elementId = getTipElementId(tip);
    if (elementId) {
      tips
        .filter(
          (other) => other.id !== id && getTipElementId(other) === elementId,
        )
        .forEach((other) => next.delete(other.id));
    }
    next.add(id);
  });
  return next;
}

/** A commitment's status without and with the ticked tips. */
export interface SimulatedCommitment {
  action: ReductionActionCode;
  before: CommitmentStatus;
  after: CommitmentStatus;
}

/**
 * Each commitment without and with the ticked tips. An improvable one is
 * respected once every tip it raised is settled: ticked, or — for a
 * direct-flight tip — its flight replaced by a ticked ground alternative,
 * which takes the stopover away with the flight. `updateTipSelection()`
 * unticks the direct-flight tip in that case, so it cannot be read from the
 * selection alone. A direct flight settles nothing but itself: the trip still
 * flies.
 */
export function simulateCommitments(
  commitments: readonly TripCommitment[],
  tips: readonly ReductionTip[],
  selected: ReadonlySet<string>,
): SimulatedCommitment[] {
  const replacedFlights = new Set(
    tips
      .filter(
        (tip) => tip.type === 'transport_alternative' && selected.has(tip.id),
      )
      .map(getTipElementId),
  );
  const isSettled = (tip: ReductionTip) =>
    selected.has(tip.id) ||
    (tip.type === 'direct_flight' && replacedFlights.has(getTipElementId(tip)));

  return commitments.map(({ action, status }) => {
    const raised = tips.filter((tip) => tip.type === action);
    const after: CommitmentStatus =
      status === 'improvable' && raised.length > 0 && raised.every(isSettled)
        ? 'respected'
        : status;
    return { action, before: status, after };
  });
}

/** The trip's footprint once a set of savings is taken. */
export interface SavingsSimulation {
  /** kg CO₂e of the trip as it stands. */
  baseTotal: number;
  /** kg CO₂e avoided, capped category by category. */
  avoided: number;
  newTotal: number;
  avoidedPercent: number;
  /** Avoided kg CO₂e per breakdown type, never above what the type emits. */
  avoidedByType: Record<string, number>;
}

/**
 * Applies savings to the breakdown of `getOverviewDetailedBreakdown()`. A
 * category can never drop below zero, so each saving is capped at what its
 * category emits — that keeps the reduced slices plus the avoided share equal
 * to the base total, which the simulated donut relies on.
 */
export function simulateSavings(
  overview: {
    totalImpact: number;
    detail: ReadonlyArray<{ type: string; impact: number }>;
  },
  savingsByType: Record<string, number>,
): SavingsSimulation {
  const avoidedByType: Record<string, number> = {};
  overview.detail.forEach((item) => {
    const saved = savingsByType[item.type];
    if (saved && saved > 0) {
      avoidedByType[item.type] = Math.min(saved, item.impact);
    }
  });

  const baseTotal = overview.totalImpact;
  const avoided = Object.values(avoidedByType).reduce(
    (total, value) => total + value,
    0,
  );

  return {
    baseTotal,
    avoided,
    newTotal: Math.max(0, baseTotal - avoided),
    avoidedPercent: baseTotal > 0 ? Math.round((avoided / baseTotal) * 100) : 0,
    avoidedByType,
  };
}

export interface TipSaving {
  /** Breakdown key the saving applies to: 'plane', 'accommodationImpact'… */
  overviewType: string | null;
  /** kg CO₂e avoided over the whole trip (all travellers). */
  savings: number;
  /** Trip element concerned, used to deduplicate overlapping tips. */
  elementId: string | null;
}

export interface TipSavingContext {
  travellers: number;
  /** Maps a transport type to its breakdown category ('plane', 'train'…). */
  transportCategoryOf: (transportType: string) => string | undefined;
}

/**
 * The figures and itinerary behind a choice, or undefined when its mode is not
 * on offer — a missing mode has no figure and must never read as zero.
 *
 * An index past the end falls back on the default itinerary. `load()` clears
 * the choices with the tips, so this is only a guard.
 */
export function getAlternative(
  data: TransportAlternativeTipData,
  choice: AlternativeChoice,
): ChosenAlternative | undefined {
  const alternative = data.alternatives[choice.mode];
  if (!alternative) return undefined;

  const option =
    alternative.routes?.[choice.routeIndex] ?? alternative.routes?.[0];
  if (option) return option;
  return {
    emissionsPerPerson: alternative.emissionsPerPerson,
    savingsPercent: alternative.savingsPercent,
    route: null,
  };
}

/** The modes the API sent, in catalogue order. */
export function getAvailableAlternativeModes(
  data: TransportAlternativeTipData,
): AlternativeMode[] {
  return ALTERNATIVE_MODES.filter((mode) => data.alternatives[mode]);
}

/**
 * The choice preselected for the user: the mode on offer emitting the least
 * on its default itinerary, or null when the tip offers none.
 */
export function getDefaultChoice(
  data: TransportAlternativeTipData,
): AlternativeChoice | null {
  const best = getAvailableAlternativeModes(
    data,
  ).reduce<AlternativeMode | null>(
    (best, mode) =>
      best === null ||
      data.alternatives[mode]!.emissionsPerPerson <
        data.alternatives[best]!.emissionsPerPerson
        ? mode
        : best,
    null,
  );
  return best ? { mode: best, routeIndex: 0 } : null;
}

/**
 * Saving of a single tip, expressed on the same basis as
 * `getOverviewDetailedBreakdown()` — i.e. totals for the whole trip.
 *
 * The API is not homogeneous: `transport_alternative` and `direct_flight`
 * carry per-traveller values (it divides the impact by the travellers) while
 * `eco_accommodation` already carries trip totals.
 */
export function getTipSaving(
  tip: ReductionTip,
  ctx: TipSavingContext,
  choice?: AlternativeChoice,
): TipSaving {
  const travellers = Math.max(1, ctx.travellers);

  switch (tip.type) {
    case 'transport_alternative': {
      const data = tip.data as TransportAlternativeTipData;
      // Same resolution as `applyReductionTips`, so the saving announced here
      // is the one the applied trip lands on.
      const chosen = choice ?? getDefaultChoice(data);
      const alternative = chosen ? getAlternative(data, chosen) : undefined;
      if (!alternative) {
        return { overviewType: null, savings: 0, elementId: data.transportId };
      }
      const perPerson =
        data.currentEmissionsPerPerson - alternative.emissionsPerPerson;
      return {
        overviewType: transportOverviewType(data.transportType, ctx),
        savings: Math.max(0, perPerson) * travellers,
        elementId: data.transportId,
      };
    }
    case 'direct_flight': {
      const data = tip.data as DirectFlightTipData;
      return {
        overviewType: transportOverviewType(data.transportType, ctx),
        savings: Math.max(0, data.savings) * travellers,
        elementId: data.transportId,
      };
    }
    case 'eco_accommodation': {
      const data = tip.data as EcoAccommodationTipData;
      return {
        overviewType: 'accommodationImpact',
        savings: Math.max(0, data.savings),
        elementId: data.accommodationId,
      };
    }
    default:
      return { overviewType: null, savings: 0, elementId: null };
  }
}

/**
 * The largest saving of each trip element — what every tip together could
 * avoid, a multi-stop flight carrying two tips it can only take one of.
 * `updateTipSelection()` applies the same rule to the selection.
 */
export function bestSavingPerElement(savings: TipSaving[]): TipSaving[] {
  const bestByElement = new Map<string, TipSaving>();

  savings.forEach((saving, index) => {
    if (!saving.overviewType || saving.savings <= 0) return;
    const key = saving.elementId ?? `#${index}`;
    const current = bestByElement.get(key);
    if (!current || saving.savings > current.savings) {
      bestByElement.set(key, saving);
    }
  });

  return [...bestByElement.values()];
}

/** Total avoided per breakdown category. */
export function sumSavingsByOverviewType(
  savings: TipSaving[],
): Record<string, number> {
  const totals: Record<string, number> = {};
  savings.forEach(({ overviewType, savings: saved }) => {
    if (!overviewType || saved <= 0) return;
    totals[overviewType] = (totals[overviewType] ?? 0) + saved;
  });
  return totals;
}

/**
 * Both transport tips only ever target flights, so 'plane' is the safe
 * fallback when the category cannot be resolved.
 */
function transportOverviewType(
  transportType: string,
  ctx: TipSavingContext,
): string {
  return ctx.transportCategoryOf(transportType) ?? 'plane';
}
